// @flow

import * as React from 'react';

import { connect } from 'react-redux';
import { compose } from 'redux';

import { withPageMeta } from '@gandi/app-runner';
import { needsAuth } from '@gandi/authentication';
import { selectOneProductById, type DomainProduct } from '@gandi/cart';
import {
  fetchOneOrganization,
  selectIsLoadedOneOrganization,
  selectIsFailureOneOrganization,
  selectIsFetchingOneOrganization,
  selectOneOrganization,
  type Organization,
} from '@gandi/redux-organization';
import { withTranslator, type TranslatorProps } from '@gandi/translate';
import { Spinner } from '@gandi/ui';

import { selectSetupOwner } from 'src/Cart/Setup/modules/selectors';
import { Intro } from 'src/Layout/components/Intro';
import { selectOverviewUrl } from 'src/SimpleHosting/modules/urls';
import { fetchSmdEntries } from 'src/Tmch/modules/actions';
import {
  selectSmd,
  selectShouldFetchSmd,
  selectIsFetchingSmd,
} from 'src/Tmch/modules/selector';
import { Layout } from 'src/components/Layout';
import { withRouter } from 'src/helpers/withRouter';
import type {
  FullState,
  MapDispatchToProps,
  PreFetchArgs,
  SMD,
  PreFetchReturn,
  WithNeedsAuthProps,
  WithRouterProps,
} from 'src/types';

import ActionsContainer from './components/ActionsContainer';
import InfoContainer from './components/InfoContainer';
import InputContainer from './components/InputContainer';

type Params = {
  productId: string,
  cartId: string,
};

type InjectedProps = {
  translator: TranslatorProps,
  ...WithNeedsAuthProps,
  ...WithRouterProps<Params>,
};

type StateProps = {
  product: DomainProduct,
  fqdn: string,
  overview: string,
  smdEntries: Array<SMD>,
  shouldFetchSmd: boolean,
  isFetching: boolean,
  shouldFetchOrganization: boolean,
  setupOwner: string,
  setupOwnerOrga?: Organization,
};

type DispatchProps = {
  fetchSmdEntries: () => Promise<>,
  fetchOneOrganization: (orgId: string) => Promise<>,
};

type Props = {
  ...InjectedProps,
  ...StateProps,
  ...DispatchProps,
};

type State = {
  checking: boolean,
  selectedSmdId?: string,
  removedByUser: boolean,
};

class Smd extends React.Component<Props, State> {
  state: State = {
    checking: false,
    selectedSmdId: undefined,
    removedByUser: false,
  };

  setChecking: (newVal: boolean) => void = (newVal: boolean) => {
    if (this.state.checking !== newVal) {
      this.setState({ checking: newVal });
    }
  };

  setSelectedSmdId: (newVal: string) => void = (newVal: string) => {
    if (this.state.selectedSmdId !== newVal) {
      this.setState({ selectedSmdId: newVal });
    }
  };

  setRemovedByuser: (newVal: boolean) => void = (newVal: boolean) => {
    if (this.state.removedByUser !== newVal) {
      this.setState({ removedByUser: newVal });
    }
  };

  static preFetch({ dispatch }: PreFetchArgs<Props>): PreFetchReturn<{}> {
    return dispatch(fetchSmdEntries());
  }

  componentDidMount() {
    if (this.props.shouldFetchSmd) {
      this.props.fetchSmdEntries();
    }

    if (this.props.shouldFetchOrganization) {
      this.props.fetchOneOrganization(this.props.setupOwner);
    }
  }

  componentDidUpdate(): boolean {
    // redidect to the cart overview
    // when user remove concerned product
    // from the cart widget
    if (!this.props.product) {
      // TOCHECK
      return this.props.navigate(this.props.overview);
    }

    return true;
  }

  render(): React.Node {
    const {
      translator: { __ },
      isFetching,
      product,
      smdEntries,
      setupOwnerOrga,
    } = this.props;

    return (
      <Layout>
        <Intro title={__('Trademark claim')} />
        <InfoContainer fqdn={product.fqdn || ''} />
        {isFetching && <Spinner centered />}
        {!isFetching && smdEntries && setupOwnerOrga && (
          <InputContainer
            setupOwnerOrga={setupOwnerOrga}
            checking={this.state.checking}
            product={product}
            setSelectedSmdId={this.setSelectedSmdId}
            setChecking={this.setChecking}
            removedByUser={this.state.removedByUser}
            setRemovedByUser={this.setRemovedByuser}
          />
        )}
        <ActionsContainer
          product={product}
          checking={this.state.checking}
          selectedSmdId={this.state.selectedSmdId}
          removedByUser={this.state.removedByUser}
        />
      </Layout>
    );
  }
}

const mapStateToProps = (
  state: FullState,
  ownProps: WithRouterProps<Params>,
): StateProps => {
  // WARN: product may be undefined if user removes it from the cart widget
  const product = selectOneProductById(state, {
    productId: ownProps.params.productId,
  });

  const fqdn = product?.payload.fqdn || '';

  const overview = selectOverviewUrl(state);
  const smdEntries = selectSmd(state);

  const setupOwner = selectSetupOwner(state);
  const setupOwnerOrga = selectOneOrganization(state, setupOwner);

  // $FlowFixMe refine @gandi/cart DecoratedProduct
  return {
    // $FlowFixMe[incompatible-return]
    product,
    fqdn,
    overview,
    smdEntries,
    shouldFetchSmd: selectShouldFetchSmd(state),
    shouldFetchOrganization:
      !selectIsLoadedOneOrganization(state, setupOwner) &&
      !selectIsFailureOneOrganization(state, setupOwner) &&
      !selectIsFetchingOneOrganization(state, setupOwner),
    setupOwner,
    // $FlowFixMe[incompatible-return]
    setupOwnerOrga,
    isFetching:
      !smdEntries ||
      (smdEntries.length > 0 && !setupOwnerOrga) ||
      selectIsFetchingSmd(state) ||
      selectIsFetchingOneOrganization(state, setupOwner),
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps> = {
  fetchSmdEntries,
  fetchOneOrganization,
};

export default (compose(
  needsAuth,
  withRouter,
  withPageMeta(args => ({
    title: args.__('Manage your SMDs - Gandi.net'),
  })),
  withTranslator({ propsNamespace: 'translator' }),
  connect(mapStateToProps, mapDispatchToProps),
)(Smd): React.ComponentType<{}>);
